import { useOrganization, useUser } from '@clerk/clerk-react'
import type { ExternalAccountResource, SessionWithActivitiesResource } from '@clerk/types'
import { useFlag } from '@unleash/proxy-client-react'
import {
  Building2,
  Bell,
  Camera,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  GraduationCap,
  KeyRound,
  Languages,
  Link2,
  Loader2,
  PersonStanding,
  Plus,
  Shield,
  ShieldCheck,
  User,
} from 'lucide-react'
import { type ComponentType, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import LanguagePreferences from '@/components/LanguagePreferences'
import { AccessibilityPreferences } from '@/components/account/AccessibilityPreferences'
import { ChangePasswordForm } from '@/components/account/ChangePasswordForm'
import { NeedsAnalysisSection } from '@/components/account/NeedsAnalysisSection'
import { NotificationPreferences } from '@/components/account/NotificationPreferences'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { useAuthContext } from '@/hooks/data/useAuthContext'
import { useNeedsAnalysis } from '@/hooks/data/needs-analysis/useNeedsAnalysis'
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences'
import { useNotificationPreferencesAccess } from '@/hooks/useNotificationPreferencesAccess'
import { cn } from '@/lib/utils'

type Props = {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly onNeedsAnalysisOpen?: () => void
  readonly initialTab?: AccountDialogTab
}

export type AccountDialogTab =
  | 'profile'
  | 'security'
  | 'language'
  | 'accessibility'
  | 'needs-analysis'
  | 'notifications'

function AccessibilityTabIcon({ className }: { readonly className?: string }) {
  return (
    <span className={cn('inline-flex items-center justify-center rounded-full border-1 border-current', className)}>
      <PersonStanding className='h-3 w-3' strokeWidth={2.5} />
    </span>
  )
}

function ProfileTab() {
  const { t } = useTranslation()
  const { user: clerkUser } = useUser()
  const { organization } = useOrganization()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const canUploadImage = !!clerkUser

  const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
  const MIN_DIMENSION = 200
  const MAX_DIMENSION = 4096

  const loadImage = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        resolve(img)
      }
      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        reject(new Error('Failed to load image'))
      }
      img.src = URL.createObjectURL(file)
    })

  const validateImage = (img: HTMLImageElement): string | null => {
    if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
      return t('accountDialog.imageUploadTooSmall')
    }
    if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION) {
      return t('accountDialog.imageUploadTooBig')
    }
    return null
  }

  const cropToSquare = (img: HTMLImageElement, mimeType: string): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const size = Math.min(img.width, img.height)
      const offsetX = (img.width - size) / 2
      const offsetY = (img.height - size) / 2
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Canvas not supported'))
        return
      }
      ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size)
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Failed to create blob'))), mimeType, 0.92)
    })

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !clerkUser) return

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('accountDialog.imageUploadTooLarge'))
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setUploading(true)
    try {
      const img = await loadImage(file)
      const error = validateImage(img)
      if (error) {
        toast.error(error)
        return
      }

      let uploadFile: Blob | File = file
      if (img.width !== img.height) {
        uploadFile = await cropToSquare(img, file.type || 'image/png')
      }

      await clerkUser.setProfileImage({ file: uploadFile })
      toast.success(t('accountDialog.imageUploadSuccess'))
    } catch {
      toast.error(t('accountDialog.imageUploadError'))
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='mb-2 text-2xl font-bold text-gray-900'>{t('accountDialog.profileTab')}</h2>
        <p className='text-sm text-gray-600'>{t('accountDialog.profileDescription')}</p>
      </div>

      <div className='flex items-center gap-4'>
        <div className='relative'>
          {clerkUser?.imageUrl ? (
            <img src={clerkUser.imageUrl} alt='' className='h-14 w-14 rounded-full object-cover' />
          ) : (
            <div className='flex h-14 w-14 items-center justify-center rounded-full bg-gray-200'>
              <User className='h-6 w-6 text-gray-500' />
            </div>
          )}
          {canUploadImage && (
            <>
              <input
                ref={fileInputRef}
                type='file'
                accept='image/png,image/jpeg,image/gif,image/webp'
                className='hidden'
                onChange={handleImageUpload}
              />
              <button
                type='button'
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className='absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-gray-700 text-white transition-colors hover:bg-gray-600'
              >
                {uploading ? <Loader2 className='h-3 w-3 animate-spin' /> : <Camera className='h-3 w-3' />}
              </button>
            </>
          )}
        </div>
        <div>
          <p className='text-base font-medium'>{clerkUser?.fullName}</p>
          <p className='text-sm text-muted-foreground'>{clerkUser?.primaryEmailAddress?.emailAddress}</p>
        </div>
      </div>

      {organization && (
        <div className='flex items-center gap-3 rounded-lg border border-gray-300 p-4'>
          {organization.imageUrl ? (
            <img src={organization.imageUrl} alt='' className='h-8 w-8 rounded' />
          ) : (
            <Building2 className='h-5 w-5 text-muted-foreground' />
          )}
          <div>
            <p className='text-xs text-muted-foreground'>{t('accountDialog.organization')}</p>
            <p className='text-sm font-medium'>{organization.name}</p>
          </div>
        </div>
      )}
    </div>
  )
}

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
}

function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider.replace(/_/g, ' ')
}

// Mirrors the backend `_GOOGLE_OAUTH_EMAIL_DOMAINS` check in user_service.py
// and the same helper in InviteUserModal.tsx. Only users on these domains are
// expected to have a Google identity tied to their EnGen email.
const GOOGLE_OAUTH_DOMAINS = ['gmail.com', 'googlemail.com'] as const

function isGoogleOAuthEmail(email: string | undefined): boolean {
  if (!email) return false
  const at = email.lastIndexOf('@')
  if (at === -1) return false
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase()
  return (GOOGLE_OAUTH_DOMAINS as readonly string[]).includes(domain)
}

function ConnectedAccountsCard({
  externalAccounts,
  orgHasSso,
}: {
  readonly externalAccounts: ExternalAccountResource[]
  readonly orgHasSso: boolean
}) {
  const { t } = useTranslation()
  const { user: clerkUser } = useUser()
  const [connecting, setConnecting] = useState(false)

  const hasGoogle = externalAccounts.some((a) => a.provider === 'google')
  // Hide the consumer Google OAuth CTA for users in orgs that have an
  // enterprise SSO connection configured. Letting them connect a personal
  // Google account here bypasses their IdP and creates two parallel sign-in
  // paths — confusing and inconsistent with how their org wants them to auth.
  // Also hide for non-gmail/googlemail addresses to match the invite-flow
  // policy: only those domains are routed through Google OAuth at sign-in,
  // so recommending the link elsewhere is misleading.
  const isGoogleEligible = isGoogleOAuthEmail(clerkUser?.primaryEmailAddress?.emailAddress)
  const showConnectGoogle = !hasGoogle && !orgHasSso && isGoogleEligible

  // If the user has no linked accounts and no eligible provider to connect,
  // there's nothing actionable here — hide the card entirely rather than
  // showing a header + description with no controls underneath.
  if (externalAccounts.length === 0 && !showConnectGoogle) {
    return null
  }

  const handleConnectGoogle = async () => {
    if (!clerkUser) return
    setConnecting(true)
    try {
      const externalAccount = await clerkUser.createExternalAccount({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/auth/sso-callback`,
      })
      const verificationUrl = externalAccount.verification?.externalVerificationRedirectURL?.toString()
      if (verificationUrl) {
        window.location.href = verificationUrl
      }
    } catch {
      toast.error(t('accountDialog.connectError'))
      setConnecting(false)
    }
  }

  return (
    <div className='rounded-lg border p-4'>
      <div className='mb-3 flex items-start justify-between gap-2'>
        <div>
          <div className='flex items-center gap-2'>
            <p className='text-sm font-medium'>{t('accountDialog.connectedAccounts')}</p>
            <span className='rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary'>
              {t('accountDialog.recommendedBadge')}
            </span>
          </div>
          <p className='mt-0.5 text-xs text-muted-foreground'>{t('accountDialog.connectedAccountsDescription')}</p>
        </div>
      </div>

      <ul className='space-y-2'>
        {externalAccounts.map((account) => (
          <li key={account.id} className='flex items-center gap-3 rounded-md bg-gray-50 px-3 py-2'>
            <Link2 className='h-5 w-5 text-gray-500' />
            <div>
              <p className='text-sm font-medium capitalize'>{providerLabel(account.provider)}</p>
              <p className='text-xs text-muted-foreground'>{account.emailAddress}</p>
            </div>
          </li>
        ))}
      </ul>

      {showConnectGoogle && (
        <Button variant='manage' size='sm' className='mt-3' onClick={handleConnectGoogle} disabled={connecting}>
          {connecting ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
          {t('accountDialog.connectAccount', { provider: 'Google' })}
        </Button>
      )}
    </div>
  )
}

function ManagedAccountNotice({
  icon,
  title,
  description,
}: {
  readonly icon: React.ReactNode
  readonly title: string
  readonly description: string
}) {
  return (
    <div className='flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4'>
      <div className='shrink-0 text-blue-600'>{icon}</div>
      <div>
        <p className='text-sm font-medium text-blue-900'>{title}</p>
        <p className='mt-1 text-xs text-blue-800'>{description}</p>
      </div>
    </div>
  )
}

function SecurityTab({ showPasswordCard }: { readonly showPasswordCard: boolean }) {
  const { t } = useTranslation()
  const { user: clerkUser } = useUser()
  const { data: authContext, isLoading: authContextLoading } = useAuthContext()
  const [editing, setEditing] = useState(false)
  const [sessions, setSessions] = useState<SessionWithActivitiesResource[]>([])

  useEffect(() => {
    if (clerkUser) {
      clerkUser
        .getSessions()
        .then(setSessions)
        .catch(() => {})
    }
  }, [clerkUser])

  const latestSession = sessions.find((s) => s.status === 'active')
  const activity = latestSession?.latestActivity
  const passwordEnabled = !!clerkUser?.passwordEnabled
  const externalAccounts = clerkUser?.externalAccounts ?? []
  const hasExternalAccount = externalAccounts.length > 0

  // password_auth_disabled is org policy (ENG-3270) and outranks the per-user
  // signals: in an organization that forbids password credentials, credential
  // management is hidden for everyone, including learners who have never been
  // recognised as SSO users. Without it the card renders off managed_by_sso
  // alone, which is per-user and misses most of a SAML org's learners.
  const externallyManaged =
    !!authContext?.managed_by_lti || !!authContext?.managed_by_sso || !!authContext?.password_auth_disabled
  // Until /me/auth-context resolves, we don't know whether the user is in an
  // LTI/SAML-managed org. Showing the credential UI in that window would be a
  // brief flash for managed users (and the "Set password" link would even be
  // actionable). Render a lightweight loading state until we know.
  const showCredentialManagement = !authContextLoading && !externallyManaged

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='mb-2 text-2xl font-bold text-gray-900'>{t('accountDialog.securityTab')}</h2>
        <p className='text-sm text-gray-600'>{t('accountDialog.securityDescription')}</p>
      </div>

      {authContextLoading && (
        <div className='flex items-center justify-center py-8' role='status' aria-label='Loading sign-in methods'>
          <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
        </div>
      )}

      {authContext?.managed_by_lti && (
        <ManagedAccountNotice
          icon={<GraduationCap className='h-5 w-5' />}
          title={t('accountDialog.managedByLtiTitle')}
          description={t('accountDialog.managedByLtiDescription', {
            organization: authContext.organization_name ?? 'your organization',
          })}
        />
      )}

      {!authContext?.managed_by_lti && authContext?.managed_by_sso && (
        <ManagedAccountNotice
          icon={<ShieldCheck className='h-5 w-5' />}
          title={t('accountDialog.managedBySsoTitle')}
          description={t('accountDialog.managedBySsoDescription')}
        />
      )}

      {/* The policy is deliberately independent of the SAML config, so it can
          be set for an organization with no IdP at all. Pointing those users at
          an identity provider that does not exist would be worse than saying
          nothing, hence its own copy rather than reusing the SSO notice. */}
      {!authContext?.managed_by_lti && !authContext?.managed_by_sso && authContext?.password_auth_disabled && (
        <ManagedAccountNotice
          icon={<Shield className='h-5 w-5' />}
          title={t('accountDialog.passwordsDisabledTitle')}
          description={t('accountDialog.passwordsDisabledDescription')}
        />
      )}

      {showCredentialManagement && showPasswordCard && editing && (
        <div className='rounded-lg border border-gray-200 bg-white p-4'>
          <ChangePasswordForm onCancel={() => setEditing(false)} />
        </div>
      )}

      {showCredentialManagement && !editing && (
        <div className='space-y-3'>
          <div>
            <h3 className='text-sm font-semibold text-gray-900'>{t('accountDialog.signInMethodsTitle')}</h3>
            <p className='text-xs text-muted-foreground'>{t('accountDialog.signInMethodsDescription')}</p>
          </div>

          {clerkUser && (
            <ConnectedAccountsCard externalAccounts={externalAccounts} orgHasSso={!!authContext?.org_has_sso} />
          )}

          {showPasswordCard && passwordEnabled && (
            <div className='flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4'>
              <div className='flex items-center gap-3'>
                <KeyRound className='h-7 w-7 text-gray-500' strokeWidth={2} />
                <div>
                  <p className='text-sm font-medium'>{t('accountDialog.password')}</p>
                  <p className='text-xs text-muted-foreground'>••••••••</p>
                </div>
              </div>
              <Button variant='manage' size='sm' onClick={() => setEditing(true)}>
                {t('accountDialog.changePasswordButton')}
              </Button>
            </div>
          )}

          {showPasswordCard && !passwordEnabled && !hasExternalAccount && (
            <div className='flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4'>
              <div className='flex items-center gap-3'>
                <KeyRound className='h-7 w-7 text-gray-500' strokeWidth={2} />
                <div>
                  <p className='text-sm font-medium'>{t('accountDialog.password')}</p>
                  <p className='text-xs text-muted-foreground'>{t('accountDialog.passwordNotSet')}</p>
                </div>
              </div>
              <Button variant='manage' size='sm' onClick={() => setEditing(true)}>
                {t('accountDialog.setPasswordButton')}
              </Button>
            </div>
          )}

          {showPasswordCard && !passwordEnabled && hasExternalAccount && (
            <button
              type='button'
              onClick={() => setEditing(true)}
              className='group inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-primary'
            >
              <Plus className='h-3.5 w-3.5 transition-transform group-hover:scale-110' />
              <span className='underline-offset-2 group-hover:underline'>{t('accountDialog.addPasswordLink')}</span>
            </button>
          )}

          <p className='text-xs text-muted-foreground'>{t('accountDialog.signInFallbackNote')}</p>
        </div>
      )}

      {activity && !editing && (
        <div className='space-y-3 rounded-lg border border-gray-300 p-4'>
          <h3 className='text-sm font-medium'>{t('accountDialog.lastSignInActivity')}</h3>
          <div className='grid grid-cols-2 gap-y-2 text-sm'>
            {clerkUser?.lastSignInAt && (
              <>
                <span className='text-muted-foreground'>{t('accountDialog.signedInAt')}</span>
                <span>{clerkUser.lastSignInAt.toLocaleString()}</span>
              </>
            )}
            {authContext?.last_sign_in_method && (
              <>
                <span className='text-muted-foreground'>{t('accountDialog.signInMethod')}</span>
                <span>{t(`accountDialog.signInMethod_${authContext.last_sign_in_method}`)}</span>
              </>
            )}
            {activity.deviceType && (
              <>
                <span className='text-muted-foreground'>{t('accountDialog.device')}</span>
                <span className='capitalize'>{activity.deviceType}</span>
              </>
            )}
            {activity.browserName && (
              <>
                <span className='text-muted-foreground'>{t('accountDialog.browser')}</span>
                <span>
                  {activity.browserName} {activity.browserVersion}
                </span>
              </>
            )}
            {(activity.city || activity.country) && (
              <>
                <span className='text-muted-foreground'>{t('accountDialog.location')}</span>
                <span>{[activity.city, activity.country].filter(Boolean).join(', ')}</span>
              </>
            )}
            {activity.ipAddress && (
              <>
                <span className='text-muted-foreground'>{t('accountDialog.ipAddress')}</span>
                <span>{activity.ipAddress}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export function AccountDialog({ open, onOpenChange, onNeedsAnalysisOpen, initialTab = 'profile' }: Props) {
  const { t } = useTranslation(['translation', 'notificationPreferences'])
  const { user: clerkUser } = useUser()
  // ENG-3270: the former `password-change-disabled` kill switch never existed
  // in Unleash, so it always evaluated false and gated nothing. The real
  // control is the per-organization policy on /me/auth-context, applied via
  // `externallyManaged` in SecurityTab.
  const showPasswordCard = !!clerkUser
  const showGoalsTab = useFlag('profile-goals-tab')
  const showNotificationsTab = useNotificationPreferencesAccess()
  const { data: needsAnalysis } = useNeedsAnalysis()
  const [activeTab, setActiveTab] = useState<AccountDialogTab>('profile')
  const [notificationPreferencesActive, setNotificationPreferencesActive] = useState(false)
  const notificationPreferences = useNotificationPreferences(showNotificationsTab && notificationPreferencesActive)
  const tabRefs = useRef<Partial<Record<AccountDialogTab, HTMLButtonElement | null>>>({})
  const openNeedsAnalysisOnCloseRef = useRef(false)
  // Phones show the section list and one open section in turn; sm+ shows both.
  const [mobileView, setMobileView] = useState<'list' | 'section'>('list')
  const backButtonRef = useRef<HTMLButtonElement>(null)

  // The avatar entry keeps opening Profile. Only the notification-panel footer
  // supplies Notifications as its initial tab.
  useEffect(() => {
    if (!open) return
    const nextTab = initialTab === 'notifications' && !showNotificationsTab ? 'profile' : initialTab
    setActiveTab(nextTab)
    setMobileView(nextTab === 'profile' ? 'list' : 'section')
    if (nextTab === 'notifications') setNotificationPreferencesActive(true)
  }, [initialTab, open, showNotificationsTab])

  const selectTab = (tab: AccountDialogTab) => {
    setActiveTab(tab)
    if (tab === 'notifications') setNotificationPreferencesActive(true)
  }

  const openSection = (tab: AccountDialogTab) => {
    selectTab(tab)
    setMobileView('section')
    requestAnimationFrame(() => backButtonRef.current?.focus())
  }

  const backToList = () => {
    setMobileView('list')
    requestAnimationFrame(() => tabRefs.current[activeTab]?.focus())
  }

  const tabs: { id: AccountDialogTab; label: string; icon: ComponentType<{ readonly className?: string }> }[] = [
    { id: 'profile', label: t('accountDialog.profileTab'), icon: User },
    { id: 'security', label: t('accountDialog.securityTab'), icon: Shield },
    { id: 'language', label: t('accountDialog.languageTab'), icon: Languages },
    { id: 'accessibility', label: t('accountDialog.accessibilityTab'), icon: AccessibilityTabIcon },
    ...(showGoalsTab && needsAnalysis
      ? [
          {
            id: 'needs-analysis' as AccountDialogTab,
            label: t('accountDialog.goalsTab', 'Goals'),
            icon: ClipboardList,
          },
        ]
      : []),
    ...(showNotificationsTab
      ? [
          {
            id: 'notifications' as AccountDialogTab,
            label: t('notificationPreferences:notificationPreferences.tab'),
            icon: Bell,
          },
        ]
      : []),
  ]

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % tabs.length
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + tabs.length) % tabs.length
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = tabs.length - 1
        break
      default:
        return
    }

    event.preventDefault()
    const nextTab = tabs[nextIndex]
    if (!nextTab) return
    selectTab(nextTab.id)
    tabRefs.current[nextTab.id]?.focus()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='max-h-[85vh] overflow-hidden p-0 sm:max-w-4xl'
        // Opened once focus is back on this dialog's opener, so Needs Analysis returns focus there.
        onCloseAutoFocus={() => {
          if (!openNeedsAnalysisOnCloseRef.current) return
          openNeedsAnalysisOnCloseRef.current = false
          onNeedsAnalysisOpen?.()
        }}
      >
        <div className='flex h-[80vh] flex-col sm:h-156 sm:flex-row'>
          <div
            className={cn(
              'min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50/50 p-4 sm:flex sm:w-56 sm:flex-none sm:overflow-visible sm:border-r sm:p-5',
              mobileView === 'list' ? 'flex' : 'hidden'
            )}
          >
            <h2 className='pr-10 text-lg font-semibold sm:pr-0'>{t('accountDialog.title')}</h2>
            <p className='mb-4 text-xs text-muted-foreground'>{t('accountDialog.description')}</p>
            <div
              className='flex flex-col space-y-1'
              role='tablist'
              aria-orientation='vertical'
              aria-label={t('accountDialog.title')}
            >
              {tabs.map((tab, index) => (
                <button
                  type='button'
                  key={tab.id}
                  ref={(element) => {
                    tabRefs.current[tab.id] = element
                  }}
                  id={`account-tab-${tab.id}`}
                  role='tab'
                  aria-selected={activeTab === tab.id}
                  aria-controls={activeTab === tab.id ? `account-panel-${tab.id}` : undefined}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  onClick={() => openSection(tab.id)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                  className={cn(
                    'flex min-h-12 items-center gap-3 rounded-md px-3 text-left text-base font-medium transition-colors sm:min-h-0 sm:gap-2 sm:py-2 sm:text-sm',
                    activeTab === tab.id
                      ? 'text-foreground sm:bg-white sm:shadow-sm'
                      : 'text-foreground hover:bg-white/60 sm:text-muted-foreground sm:hover:text-foreground'
                  )}
                >
                  <tab.icon className='h-4 w-4 shrink-0' />
                  <span className='min-w-0 flex-1'>{tab.label}</span>
                  <ChevronRight className='h-4 w-4 shrink-0 sm:hidden' aria-hidden />
                </button>
              ))}
            </div>
          </div>

          <div className={cn('min-h-0 flex-1 flex-col sm:flex', mobileView === 'section' ? 'flex' : 'hidden')}>
            <div className='flex h-14 shrink-0 items-center border-b pr-12 pl-2 sm:hidden'>
              <button
                ref={backButtonRef}
                type='button'
                onClick={backToList}
                aria-label={t('accountDialog.backToSections')}
                className='flex min-h-11 items-center gap-1 rounded-md px-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground'
              >
                <ChevronLeft className='h-4 w-4' aria-hidden />
                {t('accountDialog.title')}
              </button>
            </div>
            <div
              className='min-h-0 flex-1 overflow-y-auto p-4 sm:p-6'
              id={`account-panel-${activeTab}`}
              role='tabpanel'
              aria-labelledby={`account-tab-${activeTab}`}
            >
              {activeTab === 'profile' && <ProfileTab />}
              {activeTab === 'security' && <SecurityTab showPasswordCard={showPasswordCard} />}
              {activeTab === 'language' && <LanguagePreferences />}
              {activeTab === 'accessibility' && <AccessibilityPreferences />}
              {activeTab === 'notifications' && showNotificationsTab && (
                <NotificationPreferences controller={notificationPreferences} />
              )}
              {activeTab === 'needs-analysis' && needsAnalysis && (
                <NeedsAnalysisSection
                  needsAnalysis={needsAnalysis}
                  onUpdate={() => {
                    openNeedsAnalysisOnCloseRef.current = true
                    onOpenChange(false)
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
